import { History } from "lucide-react";
import { useState } from "react";
import { useGetList } from "ra-core";
import { cn } from "@/lib/utils";

import { EmptyState } from "../data-display/EmptyState";
import { SectionCard } from "../data-display/SectionCard";
import { StatusBadge } from "../data-display/StatusBadge";
import {
  RUN_STATUS_TONES,
  RUN_STEP_TONES,
  type WorkflowRun,
  type WorkflowRunStep,
  actionLabel,
} from "./workflowModel";

/**
 * Run history: what fired, when, and what each action actually did, * including "skipped" and consent "suppressed" outcomes, so automation is
 * never a black box.
 */
export const WorkflowRunsPanel = ({ workflowId }: { workflowId: string }) => {
  const [openRunId, setOpenRunId] = useState<string | null>(null);

  const { data: runs } = useGetList<WorkflowRun>("workflow_runs", {
    pagination: { page: 1, perPage: 50 },
    sort: { field: "created_at", order: "DESC" },
    filter: { workflow_id: workflowId },
  });

  return (
    <SectionCard
      icon={History}
      title="Run history"
      subtitle="Every trigger, and what each action did"
    >
      {(runs ?? []).length === 0 ? (
        <div className="py-6">
          <EmptyState
            icon={History}
            title="No runs yet"
            description="Turn the workflow on, then trigger it to see results here."
          />
        </div>
      ) : (
        <div className="flex flex-col">
          {(runs ?? []).map((run) => (
            <div
              key={run.id}
              className="border-b border-border-subtle last:border-0"
            >
              <button
                type="button"
                onClick={() =>
                  setOpenRunId(openRunId === run.id ? null : run.id)
                }
                className="flex w-full items-center justify-between gap-3 py-2.5 text-left text-[13px]"
              >
                <div className="min-w-0">
                  <p className="truncate font-medium text-foreground">
                    {new Date(run.created_at).toLocaleString()}
                  </p>
                  <p className="truncate text-[12px] text-muted-foreground">
                    {run.error
                      ? run.error
                      : run.next_run_at && run.status !== "completed"
                        ? `next action ${new Date(run.next_run_at).toLocaleString()}`
                        : `${run.current_action} action(s) run`}
                  </p>
                </div>
                <StatusBadge
                  label={run.status}
                  tone={RUN_STATUS_TONES[run.status] ?? "neutral"}
                />
              </button>
              {openRunId === run.id && <RunSteps runId={run.id} />}
            </div>
          ))}
        </div>
      )}
    </SectionCard>
  );
};

const RunSteps = ({ runId }: { runId: string }) => {
  const { data: steps } = useGetList<WorkflowRunStep>("workflow_run_steps", {
    pagination: { page: 1, perPage: 50 },
    sort: { field: "position", order: "ASC" },
    filter: { run_id: runId },
  });

  if (!steps?.length) {
    return (
      <p className="pb-3 text-[12px] text-muted-foreground">
        No actions have run yet.
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-1 pb-3">
      {steps.map((step) => (
        <div
          key={step.id}
          className={cn(
            "flex items-center justify-between gap-3 rounded-md bg-muted/40 px-2.5 py-1.5 text-[12.5px]",
          )}
        >
          <span className="min-w-0">
            <span className="font-medium">{actionLabel(step.action_type)}</span>
            {step.detail ? (
              <span className="ml-2 text-muted-foreground">{step.detail}</span>
            ) : null}
          </span>
          <StatusBadge
            label={step.status}
            tone={RUN_STEP_TONES[step.status] ?? "neutral"}
          />
        </div>
      ))}
    </div>
  );
};
